import { useEffect, useState } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../lib/api";
import { useTokenPreview } from "../lib/tokenPreview";

export default function AcceptInvite() {
  const search = useSearch();
  const token = new URLSearchParams(search).get("token");
  const preview = useTokenPreview(token, "invitation");
  const [, setLocation] = useLocation();
  const qc = useQueryClient();

  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pre-fill display name from any name the inviter provided.
  useEffect(() => {
    if (preview.status === "ready" && preview.preview.display_name) {
      setDisplayName(preview.preview.display_name);
    }
  }, [preview]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!token || password.length < 10 || password !== confirm) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiFetch("/auth/accept-invitation", {
        method: "POST",
        body: JSON.stringify({
          token,
          password,
          display_name: displayName.trim() || undefined,
        }),
      });
      await qc.invalidateQueries({ queryKey: ["me"] });
      setLocation("/overview");
    } catch (err: any) {
      setError(err?.message ?? "Could not accept invitation.");
    } finally {
      setSubmitting(false);
    }
  }

  if (preview.status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">
        Loading…
      </div>
    );
  }

  if (preview.status === "invalid") {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <h1 className="text-2xl font-bold text-slate-900">
            Invitation is invalid
          </h1>
          <p className="mt-2 text-slate-600">{preview.message}</p>
          <p className="mt-2 text-sm text-slate-500">
            Ask the operator who invited you to send a fresh invitation.
          </p>
          <p className="mt-8 text-sm">
            <Link href="/login" className="text-brand-700 hover:underline">
              Back to sign in
            </Link>
          </p>
        </div>
      </div>
    );
  }

  const mismatch = confirm.length > 0 && password !== confirm;
  const tooShort = password.length > 0 && password.length < 10;

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-slate-900">
            Welcome to Thynra
          </h1>
          <p className="mt-2 text-slate-600">
            Set up your account for <strong>{preview.preview.email}</strong>
          </p>
        </div>
        <form
          onSubmit={handleSubmit}
          className="bg-white shadow-sm rounded-xl p-6 border border-slate-200"
        >
          <label className="block">
            <span className="text-sm font-medium text-slate-700">
              Your name
            </span>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Optional"
              autoComplete="name"
              className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-brand-600 focus:outline-none focus:ring-1 focus:ring-brand-600"
            />
          </label>
          <label className="block mt-4">
            <span className="text-sm font-medium text-slate-700">
              Choose a password
            </span>
            <input
              type="password"
              required
              minLength={10}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-brand-600 focus:outline-none focus:ring-1 focus:ring-brand-600"
            />
            <span className="mt-1 block text-xs text-slate-500">
              At least 10 characters.
            </span>
          </label>
          <label className="block mt-4">
            <span className="text-sm font-medium text-slate-700">
              Confirm password
            </span>
            <input
              type="password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-brand-600 focus:outline-none focus:ring-1 focus:ring-brand-600"
            />
          </label>
          {tooShort && (
            <p className="mt-3 text-sm text-amber-600">
              Password must be at least 10 characters.
            </p>
          )}
          {mismatch && (
            <p className="mt-3 text-sm text-amber-600">
              Passwords don't match.
            </p>
          )}
          {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
          <button
            type="submit"
            disabled={
              submitting ||
              password.length < 10 ||
              password !== confirm
            }
            className="mt-5 w-full rounded-lg bg-brand-600 px-4 py-2.5 text-white font-medium hover:bg-brand-700 disabled:opacity-60 disabled:cursor-not-allowed transition"
          >
            {submitting ? "Setting up…" : "Create my account"}
          </button>
        </form>
      </div>
    </div>
  );
}
